import { Container, Section, Eyebrow } from '@/components/primitives/Container';

const SECTIONS = [
  { id: 'terminos', label: 'Términos de uso' },
  { id: 'privacidad', label: 'Política de privacidad' },
  { id: 'politica-de-fuentes', label: 'Política de fuentes' },
  { id: 'copyright', label: 'Copyright de documentos oficiales' },
  { id: 'editor', label: 'Editor responsable' },
];

export function Legal() {
  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            Lo legal y lo editorial.
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            La política de fuentes vive en esta página al mismo nivel que los términos. Es parte de
            la promesa pública de SICA, no fine print.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[14rem_1fr]">
            <aside className="hidden lg:block">
              <nav aria-label="Secciones legales" className="sticky top-24">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  Secciones
                </p>
                <ul className="mt-4 flex flex-col gap-2">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="focus-ring rounded text-sm text-ink-muted hover:text-ink"
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <article className="prose-editorial max-w-editorial">
              <Block id="terminos" title="Términos de uso">
                <p>
                  RVD.AI SICA es un servicio de consulta del sistema curricular argentino: no
                  requiere registro, no crea cuentas y no almacena contenido tuyo. Toda respuesta
                  se funda en un documento curricular identificable, con cita al documento y a la
                  página; cuando no existe un documento que responda la pregunta, el servicio lo
                  dice en lugar de inventar una respuesta. Las respuestas son material de consulta:
                  no reemplazan el criterio profesional del docente, del directivo ni del
                  especialista.
                </p>
                <p>
                  Los documentos curriculares son documentos públicos oficiales. La fuente
                  autoritativa es siempre la versión publicada por el organismo emisor: ante
                  cualquier diferencia, prevalece el documento oficial. Podés consultar, comparar y
                  citar el contenido en tu trabajo profesional; no podés presentar lo que devuelve
                  el servicio como una versión oficial ni alterar las citas que acompañan las
                  respuestas.
                </p>
                <p>
                  La versión íntegra de los términos — responsabilidades, propiedad intelectual,
                  ley aplicable — está publicada en la aplicación RVD.AI SICA y es la que rige el
                  uso del servicio.
                </p>
              </Block>

              <Block id="privacidad" title="Política de privacidad">
                <p>
                  RVD.AI SICA funciona sin registro y sin cuentas de usuario: no te pide nombre,
                  email ni ningún dato identificatorio, no usa analítica de terceros y no instala
                  cookies de seguimiento. Lo que se procesa es lo necesario para responder — el
                  texto de tus búsquedas y consultas, y los registros técnicos que todo servicio
                  web genera para operar y prevenir abusos — sin asociación a ninguna identidad,
                  porque el servicio no sabe quién sos.
                </p>
                <p>
                  En este sitio, el único punto donde te pedimos datos es el formulario de
                  contacto: nombre, email, institución si corresponde y tu mensaje. Lo que envíes
                  por ese formulario se usa únicamente para responderte — no se comparte con
                  terceros ni alimenta listas de correo o promociones.
                </p>
                <p>
                  El tratamiento se rige por la Ley 25.326 de Protección de Datos Personales
                  (Argentina). La política íntegra — qué se procesa, con qué finalidad, cuánto se
                  conserva y qué derechos te reconoce la ley — está publicada en la aplicación SICA
                  Docentes.
                </p>
              </Block>

              <Block id="politica-de-fuentes" title="Política de fuentes">
                <p>
                  Toda respuesta de SICA apunta a un documento y una página verificables. Esta es
                  una restricción técnica, no editorial: el motor no puede emitir respuestas que no
                  estén ancladas al Inventario de Activos Curriculares (IAC).
                </p>
                <p>
                  <strong>Compromiso público:</strong> si encontrás una cita incorrecta, una página
                  desplazada o un fragmento atribuido al documento equivocado, escribinos. Cada
                  reporte se revisa contra el documento fuente original y, si corresponde, se corrige
                  con un asiento auditable en el IAC.
                </p>
                <p>
                  <strong>Para integradores vía MCP:</strong> si SICA devuelve cita, tu agente debe
                  exponerla al usuario final. No es opcional.
                </p>
              </Block>

              <Block id="copyright" title="Copyright de documentos oficiales">
                <p>
                  Los documentos curriculares oficiales argentinos (NAP, diseños jurisdiccionales)
                  son obras del Estado nacional y de los Estados jurisdiccionales. SICA los referencia
                  con cita textual y enlace al portal oficial cuando está disponible. SICA reproduce
                  y estructura esos documentos con fines de consulta, siempre con cita al documento
                  oficial, y no reclama autoría ni titularidad sobre su contenido.
                </p>
              </Block>

              <Block id="editor" title="Editor responsable">
                <p>
                  Editor responsable:{' '}
                  <span className="text-ink-muted">
                    [Razón social del responsable — pendiente de definición]
                  </span>
                  , con domicilio en la República Argentina.
                </p>
                <p>Datos de contacto institucional pendientes de publicación.</p>
              </Block>
            </article>
          </div>
        </Container>
      </Section>
    </>
  );
}

function Block({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 pb-12">
      <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}
